import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import EmailIcon from "@mui/icons-material/Email";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Colors } from "Components/Layout/Themes/Colors";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import { ExportCardTransactionsDrawer } from "Components/Shared/ExportCardTransactionsDrawer";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { useExportCardTransactionsMutation } from "Api/Mutations/CardTransactions/useExportCardTransactionsMutation";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { TransactionExportFormat } from "Infrastructure/Api/Api";

const ExportResources = Resources.Components.CardTransactions.Export;

const getEmailSchema = (t: TFunction) =>
  z.object({
    email: z.email({ message: t(Resources.Validation.InvalidEmail) }),
  });

type EmailFormData = z.infer<ReturnType<typeof getEmailSchema>>;

type Props = {
  filters: CardTransactionFilters;
};

export const ExportCardTransactionsActions = ({ filters }: Props) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  const { t } = useResource();
  const theme = useTheme();
  const { mutate: exportCardTransactions, isPending } =
    useExportCardTransactionsMutation();
  const emailSchema = useMemo(() => getEmailSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset: resetEmailForm,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const handleOpenEmailDialog = () => {
    setIsEmailDialogOpen(true);
    resetEmailForm();
  };

  const handleCloseEmailDialog = () => {
    setIsEmailDialogOpen(false);
  };

  const handleSendEmail = ({ email }: EmailFormData) => {
    exportCardTransactions(
      {
        format: TransactionExportFormat.Email,
        filters,
        email,
      },
      {
        onSuccess: (result) => {
          // The backend can return 200 with emailSent:false (e.g. the email provider rejected it) — don't
          // tell the merchant it was sent when it wasn't. Keep the dialog open so they can retry.
          if (result.isEmail && !result.emailSent) {
            toast.error(t(ExportResources.EmailSentError));
            return;
          }
          toast.success(t(ExportResources.EmailSentSuccess));
          handleCloseEmailDialog();
        },
        onError: () => {
          toast.error(t(ExportResources.EmailSentError));
        },
      },
    );
  };

  return (
    <Box display="flex" gap={1}>
      <IconButton
        onClick={handleOpenEmailDialog}
        size="small"
        sx={{
          color: theme.palette.primary.main,
          "&:hover": { backgroundColor: Colors.MediumGrey },
        }}
        aria-label={t(ExportResources.Email)}
      >
        <EmailIcon />
      </IconButton>
      <IconButton
        onClick={() => setIsDrawerOpen(true)}
        size="small"
        sx={{
          color: theme.palette.primary.main,
          "&:hover": { backgroundColor: Colors.MediumGrey },
        }}
        aria-label={t(ExportResources.Download)}
      >
        <DownloadIcon />
      </IconButton>
      <ExportCardTransactionsDrawer
        isOpen={isDrawerOpen}
        onOpen={() => setIsDrawerOpen(true)}
        onClose={() => setIsDrawerOpen(false)}
        filters={filters}
      />
      <Dialog
        open={isEmailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t(ExportResources.SendToEmail)}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit(handleSendEmail)}>
          <DialogContent>
            <FormInput
              autoFocus
              control={control}
              name="email"
              label={t(ExportResources.EmailAddress)}
              placeholder={t(ExportResources.EnterEmailAddress)}
              type="email"
              valueTransformer={(v) => v.toLowerCase()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmailDialog}>
              {t(ExportResources.Cancel)}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending}
              startIcon={isPending ? <CircularProgress size={20} /> : undefined}
            >
              {t(ExportResources.Send)}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};
